import {
  hasAwsCloudContracts,
  hasAzureCloudContracts,
  hasGCPCloudContracts,
  hasOffice365CloudContracts,
} from "../../../../functions/contractHelper";
import { hasIntegrationsByType } from "../../../../functions/getCloudReportInformation";
import GlobalConstants from "@constants";
import { shallowEqual, useSelector } from "react-redux";
import { IRootState } from "../../../../modules";
import { useClaims } from "../../../../auth/ClaimsProvider";
import { CloudAccount } from "@lib/ShiOneClient";
import { IntegrationsCollectorState } from "../../../../modules/rtk/integrationsCollector/integrationsCollectorTypes";
import {
  assetItemsToShow,
  cloudAwsItemsToShow,
  cloudAzureItemsToShow,
  cloudDatadogItemsToShow,
  cloudGcpItemsToShow,
  cloudOffice365ItemsToShow,
  homeItemsToShow,
  servicesItemsToShow,
  settingsItemsToShow,
  supportItemsToShow,
} from "./NavItemsToShowHelpers";
import { useFeatureFlag } from "shared-ui/src/utils/FeatureFlagHelper";
import { ShiOnePermission } from "auth/authorizationTypes";

export function NavItemsToShow({
  cloudContracts,
  teamContracts,
  integrationsCollection,
  hasAzureCloudReports,
  fsaId,
  myManagedSecurityGroups,
}: {
  cloudContracts: CloudAccount[];
  teamContracts: any;
  integrationsCollection: IntegrationsCollectorState;
  hasAzureCloudReports: any;
  fsaId: number | undefined;
  myManagedSecurityGroups: any[];
}) {
  const {
    hasPermission,
    hasRoleIn,
    getClaimValue,
    isGuestUser,
  } = useClaims();
  const myDevices = useSelector(
    (state: IRootState) => state.device.myDevices,
    shallowEqual,
  );
  const { enabled: useTagGroupsPage } = useFeatureFlag(
    "revenue.cloud-tag-groups",
    false,
  );

  const isAdmin = hasRoleIn("CustomerAdmin", "ShiAdmin");
  const contosoDemo =
    getClaimValue("accountId") ===
    GlobalConstants.demoAccounts.contoso.toString();

  const hasDevices = myDevices.length > 0;

  const hasAzureContracts = hasAzureCloudContracts(cloudContracts);
  const hasAwsContracts = hasAwsCloudContracts(cloudContracts);
  const hasOffice365Contracts = hasOffice365CloudContracts(cloudContracts);
  const hasGcpContracts = hasGCPCloudContracts(teamContracts, cloudContracts);

  const hasCatalyst = hasPermission(ShiOnePermission.catalystRead);

  const hasAzureComplianceIntegrations = hasIntegrationsByType(
    integrationsCollection,
    GlobalConstants.integrationType.azureCompliance,
  );
  const hasAwsBillingReportIntegrations = hasIntegrationsByType(
    integrationsCollection,
    GlobalConstants.integrationType.awsBillingReport,
  );
  const hasAzureBackupIntegrations = hasIntegrationsByType(
    integrationsCollection,
    GlobalConstants.integrationType.azureBackups,
  );
  const hasAzureGovernanceReportsIntegrations = hasIntegrationsByType(
    integrationsCollection,
    GlobalConstants.integrationType.azureGovernanceReports,
  );
  const hasGcpUsageReportIntegrations = hasIntegrationsByType(
    integrationsCollection,
    GlobalConstants.integrationType.gcpUsageReport,
  );
  const hasCiscoCRRIntegration = hasIntegrationsByType(
    integrationsCollection,
    GlobalConstants.integrationType.ciscoReadyReport,
  );
  const hasCiscoSNTCIntegration = hasIntegrationsByType(
    integrationsCollection,
    GlobalConstants.integrationType.ciscoSNTC,
  );
  const hasCiscoCCIntegration = hasIntegrationsByType(
    integrationsCollection,
    GlobalConstants.integrationType.ciscoContractCoverage,
  );
  const hasCiscoEAIntegration = hasIntegrationsByType(
    integrationsCollection,
    GlobalConstants.integrationType.ciscoEnterpriseAgreement,
  );
  const hasCiscoSubIntegration = hasIntegrationsByType(
    integrationsCollection,
    GlobalConstants.integrationType.ciscoSubscriptionManagement,
  );
  const hasDatadogUsageIntegration = hasIntegrationsByType(
    integrationsCollection,
    GlobalConstants.integrationType.datadogUsageReport,
  );
  const hasDattoRmmIntegration = hasIntegrationsByType(
    integrationsCollection,
    GlobalConstants.integrationType.dattoRmm,
  );

  const hasCiscoIntegrations =
    hasCiscoCRRIntegration ||
    hasCiscoSNTCIntegration ||
    hasCiscoCCIntegration ||
    hasCiscoEAIntegration ||
    hasCiscoSubIntegration;

  const hasAzureMultiCloudReport =
    hasAzureCloudReports && hasAzureCloudReports.hasAzurePlanReportIntegration;

  const hasCloudReports =
    hasAzureMultiCloudReport ||
    hasAwsBillingReportIntegrations ||
    hasGcpUsageReportIntegrations ||
    !!hasDatadogUsageIntegration;

  const cloudAwsItemsToShowRules = cloudAwsItemsToShow(
    hasPermission,
    cloudContracts,
    isAdmin,
    isGuestUser(),
  );
  const cloudAzureItemsToShowRules = cloudAzureItemsToShow({
    hasPermission,
    hasAzureBackupIntegrations,
    hasAzureComplianceIntegrations,
    hasAzureGovernanceReportsIntegrations,
  });
  const cloudGcpItemsToShowRules = cloudGcpItemsToShow({
    hasPermission,
  });
  const cloudOffice365ItemsToShowRules =
    cloudOffice365ItemsToShow(hasPermission);
  const cloudDatadogItemsToShowRules = cloudDatadogItemsToShow(
    hasPermission,
    isAdmin,
    hasDatadogUsageIntegration,
    contosoDemo,
  );
  const showCloud =
    Object.entries(cloudAwsItemsToShowRules).some(([_k, v]) => v) ||
    Object.entries(cloudAzureItemsToShowRules).some(([_k, v]) => v) ||
    Object.entries(cloudGcpItemsToShowRules).some(([_k, v]) => v) ||
    Object.entries(cloudOffice365ItemsToShowRules).some(([_k, v]) => v) ||
    Object.entries(cloudDatadogItemsToShowRules).some(([_k, v]) => v);
  return {
    homeItemsToShow: homeItemsToShow(
      hasPermission,
      hasCloudReports,
      isAdmin,
    ),
    supportItemsToShow: supportItemsToShow(
      hasPermission,
      hasRoleIn,
      hasDevices,
    ),
    assetItemsToShow: assetItemsToShow(
      hasPermission,
      hasDattoRmmIntegration,
      hasCiscoIntegrations,
    ),
    showCloud,
    showTagGroups: useTagGroupsPage ?? false,
    cloudAwsItemsToShow: cloudAwsItemsToShowRules,
    cloudAzureItemsToShow: cloudAzureItemsToShowRules,
    cloudGcpItemsToShow: cloudGcpItemsToShowRules,
    cloudOffice365ItemsToShow: cloudOffice365ItemsToShowRules,
    cloudDatadogItemsToShow: cloudDatadogItemsToShowRules,
    servicesItemsToShow: servicesItemsToShow(
      hasPermission,
      isAdmin,
      fsaId,
      hasCatalyst,
    ),
    settingsItemsToShow: settingsItemsToShow(isAdmin, myManagedSecurityGroups),
  };
}
